function _createForOfIteratorHelper(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];

  if (!it) {
    if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
      if (it) o = it;
      var i = 0;

      var F = function () {};

      return {
        s: F,
        n: function () {
          if (i >= o.length) return {
            done: true
          };
          return {
            done: false,
            value: o[i++]
          };
        },
        e: function (e) {
          throw e;
        },
        f: F
      };
    }

    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }

  var normalCompletion = true,
      didErr = false,
      err;
  return {
    s: function () {
      it = it.call(o);
    },
    n: function () {
      var step = it.next();
      normalCompletion = step.done;
      return step;
    },
    e: function (e) {
      didErr = true;
      err = e;
    },
    f: function () {
      try {
        if (!normalCompletion && it.return != null) it.return();
      } finally {
        if (didErr) throw err;
      }
    }
  };
}

import PropTypes from "prop-types";

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.match.js";
import "core-js/modules/es.array.concat.js";
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { createElement, useEffect, useImperativeHandle, useMemo, useState, useCallback, useRef, forwardRef } from 'react';
import unified from 'unified';
import classNames from 'classnames';
import MarkdownActions, { insertText } from './markdown_actions';
import { EuiMarkdownEditorToolbar } from './markdown_editor_toolbar';
import { EuiMarkdownEditorTextArea } from './markdown_editor_text_area';
import { EuiMarkdownFormat } from './markdown_format';
import { EuiMarkdownEditorDropZone } from './markdown_editor_drop_zone';
import { useGeneratedHtmlId } from '../../services/';
import { MODE_EDITING, MODE_VIEWING } from './markdown_modes';
import { EuiModal } from '../modal';
import { EuiMarkdownContext } from './markdown_context';
import { defaultParsingPlugins, defaultProcessingPlugins, defaultUiPlugins } from './plugins/markdown_default_plugins';
import { EuiResizeObserver } from '../observer/resize_observer'; // TODO I wanted to use the useCombinedRefs
// but I can't because it's not allowed to use react hooks
// inside a callback.

var mergeRefs = function mergeRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }

  var filteredRefs = refs.filter(Boolean);
  if (!filteredRefs.length) return null;
  if (filteredRefs.length === 0) return filteredRefs[0];
  return function (inst) {
    var _iterator = _createForOfIteratorHelper(filteredRefs),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var ref = _step.value;

        if (typeof ref === 'function') {
          ref(inst);
        } else if (ref) {
          ref.current = inst;
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  };
};

function isNewLine(char) {
  if (char == null) return true;
  return !!char.match(/[\r\n]/);
}

function padWithNewlinesIfNeeded(textarea, text) {
  var selectionStart = textarea.selectionStart;
  var selectionEnd = textarea.selectionEnd; // block parsing requires two leading new lines and none trailing, but we add an extra trailing line for readability

  var isPrevNewLine = isNewLine(textarea.value[selectionStart - 1]);
  var isPrevPrevNewLine = isNewLine(textarea.value[selectionStart - 2]);
  var isNextNewLine = isNewLine(textarea.value[selectionEnd]); // pad text with newlines as needed

  text = "".concat(isPrevNewLine ? '' : '\n').concat(isPrevPrevNewLine ? '' : '\n').concat(text).concat(isNextNewLine ? '' : '\n');
  return text;
}

export var EuiMarkdownEditor = forwardRef(({
  className,
  editorId: _editorId,
  value,
  onChange,
  height = 250,
  maxHeight = 500,
  autoExpandPreview = true,
  parsingPluginList = defaultParsingPlugins,
  processingPluginList = defaultProcessingPlugins,
  uiPlugins = defaultUiPlugins,
  onParse,
  errors = [],
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  initialViewMode = MODE_EDITING,
  dropHandlers = [],
  markdownFormatProps,
  placeholder,
  readOnly,
  ...rest
}, ref) => {
  const [viewMode, setViewMode] = useState(initialViewMode);
  const editorId = useGeneratedHtmlId({
    conditionalId: _editorId
  });
  const [pluginEditorPlugin, setPluginEditorPlugin] = useState(undefined);
  const toolbarPlugins = [...uiPlugins];
  const markdownActions = useMemo(() => new MarkdownActions(editorId, toolbarPlugins), // toolbarPlugins _is_ accounted for
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [editorId, toolbarPlugins.map(({
    name
  }) => name).join(',')]);
  const parser = useMemo(() => {
    const Compiler = tree => {
      return tree;
    };

    function identityCompiler() {
      this.Compiler = Compiler;
    }

    return unified().use(parsingPluginList).use(identityCompiler);
  }, [parsingPluginList]);
  const [parsed, parseError] = useMemo(() => {
    try {
      const parsed = parser.processSync(value);
      return [parsed, null];
    } catch (e) {
      return [null, e];
    }
  }, [parser, value]);
  const isPreviewing = viewMode === MODE_VIEWING;
  const isEditing = viewMode === MODE_EDITING;
  const replaceNode = useCallback((position, next) => {
    const leading = value.substr(0, position.start.offset);
    const trailing = value.substr(position.end.offset);
    onChange(`${leading}${next}${trailing}`);
  }, [value, onChange]);
  const contextValue = useMemo(() => ({
    openPluginEditor: readOnly ? () => {} : plugin => setPluginEditorPlugin(() => plugin),
    replaceNode: readOnly ? () => {} : replaceNode,
    readOnly: readOnly
  }), [replaceNode, readOnly]);
  const [selectedNode, setSelectedNode] = useState();
  const textareaRef = useRef(null);
  useEffect(() => {
    if (textareaRef == null) return;
    if (parsed == null) return;

    const getCursorNode = () => {
      const {
        selectionStart
      } = textareaRef.current;
      let node = parsed.result ?? parsed.contents;

      outer: while (true) {
        if (node.children) {
          for (let i = 0; i < node.children.length; i++) {
            const child = node.children[i];

            if (child.position && child.position.start.offset < selectionStart && selectionStart < child.position.end.offset) {
              if (child.type === 'text') break outer; // don't dive into `text` nodes

              node = child;
              continue outer;
            }
          }
        }

        break;
      }

      setSelectedNode(node);
    }; // `parsed` changed, which means the node at the cursor may be different
    // e.g. from clicking a toolbar button


    getCursorNode();
    const textarea = textareaRef.current;
    textarea.addEventListener('keyup', getCursorNode);
    textarea.addEventListener('mouseup', getCursorNode);
    return () => {
      textarea.removeEventListener('keyup', getCursorNode);
      textarea.removeEventListener('mouseup', getCursorNode);
    };
  }, [parsed]);
  useEffect(() => {
    if (onParse) {
      const messages = parsed ? parsed.messages : [];
      const ast = parsed ? parsed.result ?? parsed.contents : null;
      onParse(parseError, {
        messages,
        ast
      });
    }
  }, [onParse, parsed, parseError]);
  useImperativeHandle(ref, () => ({
    textarea: textareaRef.current,
    replaceNode
  }), [replaceNode]);
  const textarea = textareaRef.current;
  const previewRef = useRef(null);
  const editorToolbarRef = useRef(null);
  const [hasUnacceptedItems, setHasUnacceptedItems] = React.useState(false);
  const [currentHeight, setCurrentHeight] = useState(height);
  const [editorFooterHeight, setEditorFooterHeight] = useState(0);
  const [editorToolbarHeight, setEditorToolbarHeight] = useState(0);
  const classes = classNames('euiMarkdownEditor', {
    'euiMarkdownEditor--fullHeight': height === 'full'
  }, {
    'euiMarkdownEditor--isPreviewing': isPreviewing
  }, className);
  const classesPreview = classNames('euiMarkdownEditorPreview', {
    'euiMarkdownEditorPreview-isReadOnly': readOnly
  });

  const onResize = () => {
    if (textarea && isEditing && height !== 'full') {
      const resizedTextareaHeight = textarea.offsetHeight + editorFooterHeight;
      setCurrentHeight(resizedTextareaHeight);
    }
  };

  useEffect(() => {
    setEditorToolbarHeight(editorToolbarRef.current.offsetHeight);
  }, [setEditorToolbarHeight]);
  useEffect(() => {
    if (isPreviewing && autoExpandPreview && height !== 'full') {
      if (previewRef.current.scrollHeight > currentHeight) {
        // scrollHeight does not include the border or margin
        // so we ask for the computed value for those,
        // which is always in pixels because getComputedValue
        // returns the resolved values
        const elementComputedStyle = window.getComputedStyle(previewRef.current);
        const borderWidth = parseFloat(elementComputedStyle.borderTopWidth) + parseFloat(elementComputedStyle.borderBottomWidth);
        const marginWidth = parseFloat(elementComputedStyle.marginTop) + parseFloat(elementComputedStyle.marginBottom); // then add an extra pixel for safety and because the scrollHeight value is rounded

        const extraHeight = borderWidth + marginWidth + 1;
        setCurrentHeight(previewRef.current.scrollHeight + extraHeight);
      }
    }
  }, [currentHeight, isPreviewing, height, autoExpandPreview]);
  const previewHeight = height === 'full' ? `calc(100% - ${editorFooterHeight}px)` : currentHeight;
  const textAreaHeight = height === 'full' ? '100%' : `calc(${height - editorFooterHeight}px)`;
  const textAreaMaxHeight = height !== 'full' ? `${maxHeight - editorFooterHeight}px` : ''; // safari needs this calc when the height is set to full

  const editorToggleContainerHeight = `calc(100% - ${editorToolbarHeight}px)`;
  return <EuiMarkdownContext.Provider value={contextValue}>
        <div className={classes} {...rest}>
          <EuiMarkdownEditorToolbar ref={editorToolbarRef} selectedNode={selectedNode} markdownActions={markdownActions} onClickPreview={() => setViewMode(isPreviewing ? MODE_EDITING : MODE_VIEWING)} viewMode={viewMode} uiPlugins={toolbarPlugins} />

          {isPreviewing && <div ref={previewRef} className={classesPreview} style={{
        height: previewHeight
      }}>
              <EuiMarkdownFormat parsingPluginList={parsingPluginList} processingPluginList={processingPluginList} {...markdownFormatProps}>
                {value}
              </EuiMarkdownFormat>
            </div>}
          {
        /* Toggle the editor's display instead of unmounting to retain its undo/redo history */
      }
          <div className="euiMarkdownEditor__toggleContainer" style={{
        height: editorToggleContainerHeight
      }}>
            <EuiMarkdownEditorDropZone setEditorFooterHeight={setEditorFooterHeight} isEditing={isEditing} dropHandlers={dropHandlers} insertText={(text, config) => {
          if (config.block) {
            text = padWithNewlinesIfNeeded(textareaRef.current, text);
          }

          const originalSelectionStart = textareaRef.current.selectionStart;
          const newSelectionPoint = originalSelectionStart + text.length;
          insertText(textareaRef.current, {
            text,
            selectionStart: newSelectionPoint,
            selectionEnd: newSelectionPoint
          });
        }} uiPlugins={toolbarPlugins} errors={errors} hasUnacceptedItems={hasUnacceptedItems} setHasUnacceptedItems={setHasUnacceptedItems}>
              <EuiResizeObserver onResize={onResize}>
                {resizeRef => {
              return <EuiMarkdownEditorTextArea height={textAreaHeight} maxHeight={textAreaMaxHeight} ref={mergeRefs(textareaRef, resizeRef)} id={editorId} onChange={e => onChange(e.target.value)} value={value} onFocus={() => setHasUnacceptedItems(false)} placeholder={placeholder} readOnly={readOnly} {...{
                'aria-label': ariaLabel,
                'aria-labelledby': ariaLabelledBy,
                'aria-describedby': ariaDescribedBy
              }} />;
            }}
              </EuiResizeObserver>
            </EuiMarkdownEditorDropZone>

            {pluginEditorPlugin && <EuiModal onClose={() => setPluginEditorPlugin(undefined)}>
                {createElement(pluginEditorPlugin.editor, {
            node: selectedNode && selectedNode.type === pluginEditorPlugin.name ? selectedNode : null,
            onCancel: () => setPluginEditorPlugin(undefined),
            onSave: (markdown, config) => {
              if (selectedNode && selectedNode.type === pluginEditorPlugin.name && selectedNode.position) {
                // modifying an existing node
                textareaRef.current.setSelectionRange(selectedNode.position.start.offset, selectedNode.position.end.offset);
              } else {
                // creating a new node
                if (config.block) {
                  // inject newlines if needed
                  markdown = padWithNewlinesIfNeeded(textareaRef.current, markdown);
                }
              }

              insertText(textareaRef.current, {
                text: markdown,
                selectionStart: undefined,
                selectionEnd: undefined
              });
              setPluginEditorPlugin(undefined);
            }
          })}
              </EuiModal>}
          </div>
        </div>
      </EuiMarkdownContext.Provider>;
});
EuiMarkdownEditor.propTypes = {
  className: PropTypes.string,

  /** aria-label OR aria-labelledby must be set */
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /** aria-label OR aria-labelledby must be set */
  "aria-labelledby": PropTypes.string,

  /** ID of an element describing the text editor, useful for associating error messages */
  "aria-describedby": PropTypes.string,

  /** a unique ID to attach to the textarea. If one isn't provided, a random one
       * will be generated */
  editorId: PropTypes.string,

  /** A markdown content */
  value: PropTypes.string.isRequired,

  /** callback function when markdown content is modified */
  onChange: PropTypes.func.isRequired,

  /**
       * Sets the current display mode to a read-only state. All editing gets resctricted.
       */
  readOnly: PropTypes.bool,

  /**
       * Sets the `height` in pixels of the editor/preview area or pass `full` to allow
       * the EuiMarkdownEditor to fill the height of its container.
       * When in `full` mode the vertical resize is not allowed.
       */
  height: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.oneOf(["full"])]),

  /**
       * Sets the `max-height` in pixels of the editor/preview area.
       * It has no effect when the `height` is set to `full`.
       */
  maxHeight: PropTypes.number,

  /**
       * Automatically adjusts the preview height to fit all the content and avoid a scrollbar.
       */
  autoExpandPreview: PropTypes.bool,

  /** plugins to identify new syntax and parse it into an AST node */
  parsingPluginList: PropTypes.any,

  /** plugins to process the markdown AST nodes into a React nodes */
  processingPluginList: PropTypes.any,

  /** defines UI for plugins' buttons in the toolbar as well as any modals or extra UI that provides content to the editor */
  uiPlugins: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    button: PropTypes.shape({
      label: PropTypes.string.isRequired,
      iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "alert", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "codeApp", "color", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crossInACircleFilled", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "document", "documentEdit", "documentation", "documents", "dot", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelionApp", "timeRefresh", "timeslider", "training", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenTag", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired
    }).isRequired,
    helpText: PropTypes.node,
    formatting: PropTypes.shape({
      prefix: PropTypes.string,
      suffix: PropTypes.string,
      blockPrefix: PropTypes.string,
      blockSuffix: PropTypes.string,
      multiline: PropTypes.bool,
      replaceNext: PropTypes.string,
      prefixSpace: PropTypes.bool,
      scanFor: PropTypes.string,
      surroundWithNewlines: PropTypes.bool,
      orderedList: PropTypes.bool,
      trimFirst: PropTypes.bool
    }),
    editor: PropTypes.elementType
  }).isRequired),

  /** errors to bubble up */
  errors: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.any.isRequired, PropTypes.any.isRequired]).isRequired),

  /** callback triggered when parsing results are available */
  onParse: PropTypes.func,

  /** initial display mode for the editor */
  initialViewMode: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.any.isRequired]),

  /** array defining any drag&drop handlers */
  dropHandlers: PropTypes.arrayOf(PropTypes.shape({
    supportedFiles: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    accepts: PropTypes.func.isRequired,
    getFormattingForItem: PropTypes.func.isRequired
  }).isRequired),

  /**
       * Sets the placeholder of the textarea
       */
  placeholder: PropTypes.any,

  /**
       * Further extend the props applied to EuiMarkdownFormat
       */
  markdownFormatProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,

    /**
         * Determines the text size. Choose `relative` to control the `font-size` based on the value of a parent container.
         */
    textSize: PropTypes.any
  })
};
EuiMarkdownEditor.displayName = 'EuiMarkdownEditor';

try {
  EuiMarkdownEditor.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/markdown_editor/markdown_editor.tsx',
    description: '',
    displayName: 'EuiMarkdownEditor',
    methods: [],
    props: {
      value: {
        defaultValue: null,
        description: 'A markdown content',
        name: 'value',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      placeholder: {
        defaultValue: null,
        description: 'Sets the placeholder of the textarea',
        name: 'placeholder',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'callback function when markdown content is modified',
        name: 'onChange',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: '(value: string) => void'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      height: {
        defaultValue: {
          value: '250'
        },
        description: 'Sets the `height` in pixels of the editor/preview area or pass `full` to allow\n' + 'the EuiMarkdownEditor to fill the height of its container.\n' + 'When in `full` mode the vertical resize is not allowed.',
        name: 'height',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number | "full"'
        }
      },
      readOnly: {
        defaultValue: null,
        description: 'Sets the current display mode to a read-only state. All editing gets resctricted.',
        name: 'readOnly',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      maxHeight: {
        defaultValue: {
          value: '500'
        },
        description: 'Sets the `max-height` in pixels of the editor/preview area.\n' + 'It has no effect when the `height` is set to `full`.',
        name: 'maxHeight',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      editorId: {
        defaultValue: null,
        description: "a unique ID to attach to the textarea. If one isn't provided, a random one\n" + 'will be generated',
        name: 'editorId',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      autoExpandPreview: {
        defaultValue: {
          value: 'true'
        },
        description: 'Automatically adjusts the preview height to fit all the content and avoid a scrollbar.',
        name: 'autoExpandPreview',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      parsingPluginList: {
        defaultValue: {
          value: 'getDefaultEuiMarkdownParsingPlugins()'
        },
        description: 'plugins to identify new syntax and parse it into an AST node',
        name: 'parsingPluginList',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'PluggableList<Settings>'
        }
      },
      processingPluginList: {
        defaultValue: {
          value: 'getDefaultEuiMarkdownProcessingPlugins()'
        },
        description: 'plugins to process the markdown AST nodes into a React nodes',
        name: 'processingPluginList',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'PluggableList<Settings>'
        }
      },
      uiPlugins: {
        defaultValue: {
          value: 'getDefaultEuiMarkdownUiPlugins()'
        },
        description: "defines UI for plugins' buttons in the toolbar as well as any modals or extra UI that provides content to the editor",
        name: 'uiPlugins',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiMarkdownEditorUiPlugin<any>[]'
        }
      },
      errors: {
        defaultValue: {
          value: '[]'
        },
        description: 'errors to bubble up',
        name: 'errors',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiMarkdownParseError[]'
        }
      },
      onParse: {
        defaultValue: null,
        description: 'callback triggered when parsing results are available',
        name: 'onParse',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(error: EuiMarkdownParseError, data: { messages: VFileMessage[]; ast: EuiMarkdownAstNode; }) => void'
        }
      },
      initialViewMode: {
        defaultValue: {
          value: "'editing' as const"
        },
        description: 'initial display mode for the editor',
        name: 'initialViewMode',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'MARKDOWN_MODE',
          value: [{
            value: '"editing"'
          }, {
            value: '"viewing"'
          }]
        }
      },
      dropHandlers: {
        defaultValue: {
          value: '[]'
        },
        description: 'array defining any drag&drop handlers',
        name: 'dropHandlers',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiMarkdownDropHandler[]'
        }
      },
      markdownFormatProps: {
        defaultValue: null,
        description: 'Further extend the props applied to EuiMarkdownFormat',
        name: 'markdownFormatProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Omit<EuiMarkdownFormatProps, "children" | "parsingPluginList" | "processingPluginList">'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + 'aria-label OR aria-labelledby must be set\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      ref: {
        defaultValue: null,
        description: '',
        name: 'ref',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        }],
        required: false,
        type: {
          name: 'Ref<EuiMarkdownEditorRef>'
        }
      }
    },
    extendedInterfaces: ['HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps', 'Attributes']
  };
} catch (e) {}